import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader2, RefreshCw, Search } from "lucide-react";

const LIMIT_OPTIONS = [50, 100, 200, 500];

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
}

function TopList({ title, entries, testid }) {
  const items = Object.entries(entries || {});
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{title}</div>
      {items.length === 0 ? (
        <div className="mt-3 text-xs text-zinc-500">Kayıt yok.</div>
      ) : (
        <ul className="mt-3 space-y-1 text-sm">
          {items.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between">
              <span className="font-mono text-xs text-zinc-700 truncate max-w-[70%]" title={k}>{k}</span>
              <span className="text-zinc-500">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="mt-2 font-display text-3xl font-bold text-zinc-900">
        {value === null || value === undefined ? "—" : value}
      </div>
    </div>
  );
}

export default function AdminWaitlist() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [limit, setLimit] = useState(100);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    setRefreshing(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (source.trim()) params.set("source", source.trim());
      if (utmSource.trim()) params.set("utm_source", utmSource.trim());
      params.set("limit", String(limit));
      const r = await api.get(`/admin/waitlist?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  function onSearch(e) {
    e.preventDefault();
    load();
  }

  const signups = data?.signups || [];
  const agg = data?.aggregates || {};

  return (
    <AppShell variant="admin">
      <div className="max-w-6xl mx-auto p-5 lg:p-10" data-testid="admin-waitlist">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Büyüme</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
              Bekleme Listesi
            </h1>
            <p className="mt-2 text-zinc-600 max-w-2xl">
              Ön kayıt (waitlist) girişlerinin okuma-yalnız görünümü. E-posta göndermek,
              yayın yapmak veya kayıt düzenlemek buradan mümkün değildir.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
              data-testid="waitlist-limit"
            >
              {LIMIT_OPTIONS.map((n) => (<option key={n} value={n}>Son {n}</option>))}
            </select>
            <button
              type="button" onClick={load} disabled={refreshing}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="waitlist-refresh"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>
        </div>

        <form onSubmit={onSearch} className="mt-6 grid sm:grid-cols-4 gap-2" data-testid="waitlist-filters">
          <div className="sm:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="E-posta içinde ara"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
              data-testid="waitlist-q"
            />
          </div>
          <input
            type="text" value={source} onChange={(e) => setSource(e.target.value)}
            placeholder="source (tam eşleşme)"
            className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
            data-testid="waitlist-source"
          />
          <input
            type="text" value={utmSource} onChange={(e) => setUtmSource(e.target.value)}
            placeholder="utm_source (tam eşleşme)"
            className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
            data-testid="waitlist-utm-source"
          />
          <button
            type="submit"
            className="hidden"
            aria-hidden
          />
        </form>

        {err && <div className="mt-6 rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{err}</div>}

        {!data && !err && (
          <div className="mt-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        )}

        {data && (
          <>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Toplam kayıt" value={agg.total_all_time} testid="stat-total-all-time" />
              <StatCard label="Son 24 saat" value={agg.last_24h} testid="stat-last-24h" />
              <StatCard label="Son 7 gün" value={agg.last_7d} testid="stat-last-7d" />
              <StatCard label="Filtre eşleşmesi" value={data.total} testid="stat-filter-match" />
            </div>

            <div className="mt-4 grid md:grid-cols-3 gap-4">
              <TopList title="En çok Source" entries={agg.by_source} testid="waitlist-by-source" />
              <TopList title="En çok UTM Source" entries={agg.by_utm_source} testid="waitlist-by-utm-source" />
              <TopList title="En çok UTM Campaign" entries={agg.by_utm_campaign} testid="waitlist-by-utm-campaign" />
            </div>

            <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="waitlist-list">
              <div className="px-5 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Kayıtlar (yeni → eski)
              </div>
              {signups.length === 0 ? (
                <div className="p-8 text-center text-xs text-zinc-500">Bu filtre için kayıt yok.</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                        <th className="px-4 py-2 font-semibold">Zaman</th>
                        <th className="px-4 py-2 font-semibold">E-posta</th>
                        <th className="px-4 py-2 font-semibold">Source</th>
                        <th className="px-4 py-2 font-semibold">UTM Source</th>
                        <th className="px-4 py-2 font-semibold">UTM Campaign</th>
                        <th className="px-4 py-2 font-semibold">Landing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signups.map((s) => (
                        <tr key={s.id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50" data-testid={`waitlist-row-${s.id}`}>
                          <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{fmtTs(s.created_at)}</td>
                          <td className="px-4 py-2 text-zinc-900">{s.email}</td>
                          <td className="px-4 py-2 text-xs text-zinc-700">{s.source || "—"}</td>
                          <td className="px-4 py-2 text-xs text-zinc-700">{s.utm_source || "—"}</td>
                          <td className="px-4 py-2 text-xs text-zinc-700">{s.utm_campaign || "—"}</td>
                          <td className="px-4 py-2 text-xs text-zinc-500 truncate max-w-xs" title={s.landing_path || ""}>
                            {s.landing_path || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
